<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3">{{ t('canteen.management') }}</h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2">
        <div class="p-4">
          <BaseDialog
            ref="refundDialog"
            modal-id="refundTransactionDialog"
            :title="t('canteen.refund_confirm_title')"
            :confirm-text="t('canteen.refund_confirm_button')"
            confirm-variant="warning"
            :loading="refundLoading"
            @confirm="confirmRefund"
          >
            <template #body>
              <p>{{ t('canteen.refund_confirm_message', { student: refundStudentName }) }}</p>
              <p class="mb-1">{{ t('canteen.product') }}: <strong>{{ refundProductName }}</strong></p>
              <p class="mb-1">{{ t('canteen.quantity') }}: <strong>{{ refundQuantity }}</strong></p>
              <p class="mb-1">{{ t('canteen.amount') }}: <strong>{{ refundAmount }} د.ل</strong></p>
              <p class="text-info">
                <i class="fas fa-info-circle me-1"></i>
                {{ t('canteen.refund_note') }}
              </p>
            </template>
          </BaseDialog>

          <div class="row mb-4">
            <div class="col-12">
              <label class="form-label">{{ t('canteen.search_student_label') }}</label>
              <div class="d-flex flex-column flex-md-row gap-2">
                <input 
                  type="text" 
                  class="form-control flex-grow-1" 
                  v-model="studentSearchTerm"
                  :placeholder="t('canteen.search_student_placeholder')"
                  @keyup.enter="findStudent"
                >
                <button class="btn btn-primary px-4" @click="findStudent" :disabled="searchLoading">
                  <i class="fas" :class="searchLoading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                </button>
              </div>
            </div>
            
            <div v-if="selectedStudent" class="col-12 col-md-6 mt-3">
              <div class="card bg-light h-100">
                <div class="card-body p-3">
                  <h5 class="card-title mb-2">{{ selectedStudent.first_name }} {{ selectedStudent.father_name }} {{ selectedStudent.last_name }}</h5>
                  <p class="card-text mb-1">
                    <small>{{ t('canteen.student_id') }}: {{ selectedStudent.id }}</small>
                  </p>
                  <p class="card-text mb-2">
                    {{ t('canteen.current_balance') }}: <strong>{{ selectedStudent.wallet_balance }} د.ل</strong>
                  </p>
                  <div v-if="selectedStudent.dietary_restrictions" class="alert alert-warning py-1 mb-0">
                    <small>
                      <i class="fas fa-exclamation-triangle"></i>
                      {{ t('canteen.restrictions_warning') }}: {{ selectedStudent.dietary_restrictions }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row" v-if="selectedStudent && availableProducts.length > 0" v-can="'canteen_transactions.display'">
            <div class="col-12 mb-3">
              <h5>{{ t('canteen.choose_product') }}</h5>
            </div>
            
            <div v-for="product in availableProducts" :key="product.id" 
                 class="col-6 col-sm-4 col-md-3 mb-3">
              <div 
                class="card product-item h-100" 
                :class="{ 'border-danger': hasAllergyConflict(product) }"
                @click="selectProduct(product)"
              >
                <img 
                  :src="getProductImage(product)" 
                  class="card-img-top" 
                  height="120"
                  style="object-fit: cover;"
                  @error="handleImageError"
                >
                <div class="card-body text-center">
                  <h6 class="card-title">{{ product.name }}</h6>
                  <p class="card-text text-primary fw-bold">{{ product.price }} د.ل</p>
                  <div v-if="hasAllergyConflict(product)" class="text-danger">
                    <small>
                      <i class="fas fa-exclamation-circle"></i>
                      {{ t('canteen.not_suitable') }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-4" v-if="selectedProduct && selectedStudent">
            <div class="col-12 col-md-6">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title">{{ t('canteen.purchase_details') }}</h5>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label">{{ t('canteen.product') }}: {{ selectedProduct.name }}</label>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">{{ t('canteen.price') }}: {{ selectedProduct.price }} د.ل</label>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">{{ t('canteen.quantity') }}</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model="quantity"
                      min="1"
                      :max="maxQuantity"
                    >
                  </div>
                  <div class="mb-3">
                    <strong>{{ t('canteen.total_amount') }}: {{ totalAmount }} د.ل</strong>
                  </div>
                  <button 
                    class="btn btn-primary w-100" 
                    @click="processPurchase"
                    :disabled="!canPurchase"
                  >
                    <i class="fas fa-cash-register me-2"></i>
                    {{ t('canteen.complete_purchase') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-5" v-if="selectedStudent">
            <div class="col-12">
              <h5>{{ t('canteen.student_transactions') }}</h5>
              
              <!-- Mobile View with header -->
              <div class="d-block d-md-none">
                <div class="card mb-2">
                  <div class="card-header bg-light py-2">
                    <div class="row small text-muted fw-bold">
                      <div class="col-4">{{ t('canteen.product') }}</div>
                      <div class="col-3">{{ t('canteen.date') }}</div>
                      <div class="col-2">{{ t('canteen.quantity') }}</div>
                      <div class="col-3 text-end">{{ t('canteen.amount') }}</div>
                    </div>
                  </div>
                </div>
                
                <div v-if="studentTransactions.length === 0">
                  <div class="card">
                    <div class="card-body text-center text-muted py-4">
                      {{ t('canteen.no_transactions') }}
                    </div>
                  </div>
                </div>
                
                <div v-else>
                  <div v-for="transaction in studentTransactions" :key="transaction.id" 
                       class="card mb-3">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">{{ transaction.product?.name }}</h6>
                        <span class="badge" :class="getTransactionBadgeClass(transaction)">
                          {{ getTransactionTypeText(transaction) }}
                        </span>
                      </div>
                      <div class="row text-sm">
                        <div class="col-6">
                          <small class="text-muted">{{ t('canteen.date') }}:</small>
                          <p class="mb-0">{{ formatDate(transaction.created_at) }}</p>
                        </div>
                        <div class="col-6">
                          <small class="text-muted">{{ t('canteen.quantity') }}:</small>
                          <p class="mb-0">{{ transaction.quantity }}</p>
                        </div>
                        <div class="col-6">
                          <small class="text-muted">{{ t('canteen.amount') }}:</small>
                          <p class="mb-0">{{ transaction.total_amount }} د.ل</p>
                        </div>
                        <div class="col-6 text-end">
                          <button 
                            v-if="canRefundTransaction(transaction)"
                            @click="openRefundDialog(transaction)"
                            class="btn btn-sm btn-outline-danger mt-2"
                          >
                            <i class="fas fa-undo"></i> {{ t('canteen.refund') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Desktop View -->
              <div class="d-none d-md-block">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>{{ t('canteen.date') }}</th>
                        <th>{{ t('canteen.product') }}</th>
                        <th>{{ t('canteen.quantity') }}</th>
                        <th>{{ t('canteen.amount') }}</th>
                        <th>{{ t('canteen.type') }}</th>
                        <th>{{ t('canteen.actions') }}</th>
                      </tr>
                    </thead>
                    <tbody v-if="studentTransactions.length === 0">
                      <tr>
                        <td colspan="6" class="text-center text-muted py-4">
                          {{ t('canteen.no_transactions') }}
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr v-for="transaction in studentTransactions" :key="transaction.id">
                        <td>{{ formatDate(transaction.created_at) }}</td>
                        <td>{{ transaction.product?.name }}</td>
                        <td>{{ transaction.quantity }}</td>
                        <td>{{ transaction.total_amount }} د.ل</td>
                        <td>
                          <span class="badge" :class="getTransactionBadgeClass(transaction)">
                            {{ getTransactionTypeText(transaction) }}
                          </span>
                        </td>
                        <td>
                          <button 
                            v-if="canRefundTransaction(transaction)"
                            @click="openRefundDialog(transaction)"
                            class="btn btn-sm btn-outline-danger"
                          >
                            <i class="fas fa-undo"></i> {{ t('canteen.refund') }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import BaseDialog from '@/components/common/BaseDialog.vue'
import api from '@/api'

const { t, locale } = useI18n()
const toast = useToast()
const studentSearchTerm = ref('')
const selectedStudent = ref(null)
const availableProducts = ref([])
const selectedProduct = ref(null)
const quantity = ref(1)
const studentTransactions = ref([])
const searchLoading = ref(false)
const refundDialog = ref(null)
const refundLoading = ref(false)
const refundStudentName = ref('')
const refundProductName = ref('')
const refundQuantity = ref(0)
const refundAmount = ref(0)
const currentTransaction = ref(null)

const openRefundDialog = (transaction) => {
  currentTransaction.value = transaction
  refundStudentName.value = `${selectedStudent.value.first_name} ${selectedStudent.value.father_name} ${selectedStudent.value.last_name}`
  refundProductName.value = transaction.product?.name || t('canteen.unknown_product')
  refundQuantity.value = transaction.quantity
  refundAmount.value = transaction.total_amount
  refundDialog.value.openDialog()
}

const confirmRefund = async () => {
  refundLoading.value = true;
  try {
    const resp = await api.post(`/canteen/transactions/${currentTransaction.value.id}/refund`);
    const status = resp?.status ?? resp?.data?.status ?? null;

    if (status && status >= 400) {
      if (status === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
      return toast.error(resp?.data?.message || t('canteen.refund_failed'));
    }

    toast.success(resp?.data?.message || t('canteen.refund_success'));

    const studentResponse = await api.get(`/students/${selectedStudent.value.id}`);
    selectedStudent.value.wallet_balance = studentResponse.data.data.wallet_balance;
    loadTransactions();

    refundDialog.value.closeDialog();
  } catch (error) {
    notifyError(error, 'canteen.refund_failed');
  } finally {
    refundLoading.value = false;
  }
};


function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
  return toast.error(extractApiMessage(error, fallbackKey));
}
const getProductImage = (product) => {
  if (product.image_url) {
    return product.image_url;
  }
  if (product.image && product.image.startsWith('announcements/')) {
    return `/storage/${product.image}`;
  }
  if (product.image) {
    return `/storage/${product.image}`;
  }
  return '/images/no-image.png';
}

const handleImageError = (event) => {
  event.target.src = '/images/no-image.png';
}

const findStudent = async () => {
  if (!studentSearchTerm.value.trim()) {
    toast.error(t('canteen.search_student_error'));
    return;
  }
  searchLoading.value = true;
  try {
    const response = await api.get(`/students`, { params: { search: studentSearchTerm.value }});
    if (response.data?.data?.length > 0) {
      const studentId = response.data.data[0].id;
      const studentResponse = await api.get(`/students/${studentId}`);
      selectedStudent.value = studentResponse.data.data;
      await Promise.all([loadProducts(), loadTransactions()]);
    } else {
      toast.error(t('canteen.student_not_found'));
      selectedStudent.value = null;
    }
  } catch (error) {
    notifyError(error, 'canteen.student_search_failed');
    selectedStudent.value = null;
  } finally {
    searchLoading.value = false;
  }
};


const loadProducts = async () => {
  try {
    const response = await api.get('/canteen/products');
    availableProducts.value = response.data;
  } catch (error) {
    notifyError(error, 'canteen.load_products_failed');
  }
};

const loadTransactions = async () => {
  if (!selectedStudent.value) return;
  try {
    const response = await api.get(`/canteen/students/${selectedStudent.value.id}/transactions`);
    studentTransactions.value = response.data.data || [];
  } catch (error) {
    notifyError(error, 'canteen.load_transactions_failed');
    studentTransactions.value = [];
  }
};


const selectProduct = (product) => {
  if (hasAllergyConflict(product)) {
    toast.warning(t('canteen.product_allergy_warning'))
    return
  }
  selectedProduct.value = product
  quantity.value = 1
}

const hasAllergyConflict = (product) => {
  if (!selectedStudent.value?.dietary_restrictions || !product.allergy_warnings) {
    return false
  }
  const restrictions = selectedStudent.value.dietary_restrictions.split(',').map(r => r.trim())
  const warnings = product.allergy_warnings.split(',').map(w => w.trim())
  return restrictions.some(restriction => 
    warnings.some(warning => warning.toLowerCase().includes(restriction.toLowerCase()))
  )
}

const totalAmount = computed(() => {
  if (!selectedProduct.value) return 0
  return (selectedProduct.value.price * quantity.value).toFixed(2)
})

const maxQuantity = computed(() => {
  if (!selectedStudent.value || !selectedProduct.value) return 1
  const max = Math.floor(selectedStudent.value.wallet_balance / selectedProduct.value.price)
  return Math.max(1, max)
})

const canPurchase = computed(() => {
  return selectedStudent.value && 
         selectedProduct.value && 
         quantity.value > 0 &&
         parseFloat(totalAmount.value) <= parseFloat(selectedStudent.value.wallet_balance) &&
         !hasAllergyConflict(selectedProduct.value)
})

const processPurchase = async () => {
  if (!canPurchase.value) {
    if (selectedStudent.value && selectedProduct.value && quantity.value > 0) {
      const requiredAmount = parseFloat(totalAmount.value);
      const currentBalance = parseFloat(selectedStudent.value.wallet_balance);
      if (requiredAmount > currentBalance) {
        toast.error(t('canteen.not_enough_balance', { required: requiredAmount, current: currentBalance }));
        return;
      }
    }
    toast.error(t('canteen.purchase_not_possible'));
    return;
  }

  try {
    const resp = await api.post('/canteen/purchase', {
      student_id: selectedStudent.value.id,
      product_id: selectedProduct.value.id,
      quantity: quantity.value
    });

    const status = resp?.status ?? resp?.data?.status ?? null;
    if (status && status >= 400) {
      if (status === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
      return toast.error(resp?.data?.message || t('canteen.purchase_failed'));
    }

    toast.success(resp?.data?.message || t('canteen.purchase_success'));

    const studentResponse = await api.get(`/students/${selectedStudent.value.id}`);
    selectedStudent.value.wallet_balance = studentResponse.data.data.wallet_balance;

    selectedProduct.value = null;
    loadTransactions();
  } catch (error) {
    if (error?.response?.status === 422) {
      const msg = error.response?.data?.message || t('canteen.purchase_failed');
      toast.error(msg);
    } else {
      notifyError(error, 'canteen.purchase_failed');
    }
  }
};


const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('ar-LY')
}

const getTransactionBadgeClass = (transaction) => {
  switch (transaction.type) {
    case 'purchase': return 'bg-success';
    case 'refund': return 'bg-info';
    default: return 'bg-secondary';
  }
}

const getTransactionTypeText = (transaction) => {
  switch (transaction.type) {
    case 'purchase': return t('canteen.purchase');
    case 'refund': return t('canteen.refund');
    default: return transaction.type;
  }
}

const canRefundTransaction = (transaction) => {
  const hasRefund = studentTransactions.value.some(t => 
    t.type === 'refund' && t.notes && t.notes.includes(`#${transaction.id}`)
  );
  return transaction.type === 'purchase' && !hasRefund;
}

watch(quantity, (newVal) => {
  if (newVal < 1) quantity.value = 1
  if (newVal > maxQuantity.value) quantity.value = maxQuantity.value
})
</script>

<style scoped>
.product-item {
  cursor: pointer;
  transition: all 0.3s;
}

.product-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.product-image {
  height: 120px;
  object-fit: cover;
}

@media (max-width: 576px) {
  .card-body {
    padding: 1rem;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .input-group .form-control {
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
  }
  
  .input-group .btn {
    border-radius: 0.375rem;
    width: 100%;
  }
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>